// ADR 9.9.26m §1 — the two events that make 8.9.26i's affordance answerable.
//
// 8.9.26i shipped four folds, a two-way sidebar collapse and an account menu,
// and then said in its own words that two of its four decisions rested on no
// evidence: "we have no telemetry on section engagement". Nothing in that
// change created any. These two events are the difference between a question
// written down and a question that can be answered.
//
// FILED HERE RATHER THAN IN `layout/`, and that is the same layering decision
// 9.9.26k made: one call site is inside `ui/Disclosure.tsx`, and `ui/` is a
// design system (8.9.26g), not a consumer of one feature's analytics.
// `telemetry/` is cross-cutting in the same sense `ui/` is, so the dependency
// runs `ui/ → telemetry/` and nothing points from a primitive into a feature.
// `telemetry/auth.ts` (`web_signin_cost`) is the precedent for a
// subject-specific emitter filed here. Not re-exported through
// `telemetry/index.ts`: that module is the consent/attach surface, and
// `layout/analytics.ts` is likewise imported directly by its call sites.
//
// NO `plan_id`, NO SECTION CONTENT, NO LEARNER TEXT. These events answer *how
// many, and which region*. A per-plan dimension would invite a per-plan question
// the panels are not built to answer, and 8.9.26i's own storage decision already
// says the preference is about the learner rather than the plan ("a per-plan key
// would make them re-state it on every new plan") — the wire agrees with the
// storage on that point. `emitFoldsRestored` takes a plan id and **discards
// it**: it is the latch key below, compared inside this module and never a
// property.

import { capture } from './capture'

/**
 * The countable regions, as a **closed** literal union.
 *
 * Closed in the sense `VocabVisualSlot` is: a panel reads these back as series
 * names, so a new region is an edit to this file in sight of the panel that
 * consumes it. Six from 8.9.26i; ADR 10.9.26i §5 added the tutor's two fold
 * depths — the second folding surface 9.9.26m §4 wanted a comparison against.
 * `tutor_section` and `tutor_subsection` are depths, not titles: a section's
 * title is chat content, and no chat content reaches this wire. ADR 16.9.26n
 * §2 added `plan_list`, the two-pane list cell's collapse — the sidebar's
 * shape, one column over. The panel that reads these back
 * (`feature-usage.toml`'s `region_state` breakdown) needs no edit for a
 * ninth: two new series appear once they have data.
 *
 * **Deliberately not derived from `Disclosure`'s `persistKey`**, because
 * persistence and countability are different axes, and not every counted
 * region is a `Disclosure`. ADR 28.9.26e retired `account_menu` with the
 * header's menu (a `Disclosure` with no `persistKey` — the case that argued
 * this rule) and added `home_params`, Home's generation-settings toggle, which
 * `useCollapsedChrome` counts beside a storage key it names separately. ADR
 * 29.9.26h §3 added `home_pinned_lists`, the tenth: Home's pinned row, counted
 * and deliberately not persisted.
 *
 * **Closed against this ADR's own follow-up, deliberately.** The per-passage
 * fold 8.9.26i calls "the follow-up that matters" is keyed
 * `plan-section:passages:<title>` — an unbounded set that cannot join a literal
 * union and must not become one series per passage title. Such a `Disclosure`
 * passes no region and is uncounted until that ADR mints its own vocabulary.
 */
export type ChromeRegion =
  | 'plan_section_objectives'
  | 'plan_section_vocab'
  | 'plan_section_passages'
  | 'plan_section_sets'
  | 'sidebar'
  | 'home_params'
  | 'tutor_section'
  | 'tutor_subsection'
  | 'plan_list'
  | 'home_pinned_lists'

/**
 * Where an event goes.
 *
 * `layout/analytics.ts`'s `LayoutSink` **minus the return value**: that type
 * returns `boolean` to feed the launch-delivery latch in `deliverLaunch`, and
 * there is no such latch here — so this returns `void`, matching `capture()`'s
 * own signature. (§2c's latch is a dedupe, not a delivery latch: it needs
 * nothing back from the sink.)
 */
export type ChromeSink = (name: string, properties: Record<string, unknown>) => void

/** The two names, as a closed table: a third event is an edit here, in sight. */
export const CHROME_EVENTS = {
  foldToggled: 'web_chrome_fold_toggled',
  foldsRestored: 'web_chrome_folds_restored',
} as const

// THE `web_` PREFIX IS DELIBERATE AND CONDITIONAL. This client prefixes a
// *web-only* event and shares an unprefixed name with the phones otherwise.
// Both of these are web-only by construction rather than by preference:
// Android's `CollapsibleSection` takes an `onToggle: () -> Unit` and emits
// nothing, iOS's `CollapsibleSectionView` is a chevron and a `withAnimation`
// toggle, and none of the six regions exists on a phone at all (no sidebar, no
// account popover, and `Sets` is not collapsible there). So there is no phone
// event to share a name with.

const defaultSink: ChromeSink = (name, properties) => {
  capture(name, properties)
}

let sink: ChromeSink = defaultSink

/** Test seam: watch the events without attaching an SDK. */
export function setChromeSink(replacement: ChromeSink): void {
  sink = replacement
}

/** Put 6.9.26c's `capture()` back — the production path. */
export function resetChromeSink(): void {
  sink = defaultSink
}

// Module scope, not a `useRef`: React StrictMode double-invokes effects and the
// shell remounts the `<Outlet>` at the `list_detail` boundary crossing
// (`AdaptiveShell.tsx:16-20`), so a per-component guard — a `useEffect` keyed on
// `[planId]`, which is what `usePlanViewed` does — would make "once per view"
// true in production and false under a resize. A dependency array dedupes
// re-renders of one instance; a remount hands the hook fresh state whatever it
// says. `layout/analytics.ts:87-97` records the same reasoning for its own
// latches.
let lastRestoredPlan: string | null = null

/**
 * Test-only: clears the §2c latch.
 *
 * Vitest resets modules per *file*, not per *test*, so without this one case's
 * plan id suppresses the next case's first emit. The house shape:
 * `resetLayoutAnalyticsState()` exists for the same reason.
 */
export function resetChromeTelemetryState(): void {
  lastRestoredPlan = null
}

/**
 * A learner folded or unfolded something, in either direction.
 *
 * `region_state` is the two joined by a **colon**, and it is not cosmetic:
 * `InsightQuery::breakdown` is an `Option<String>` — one property name — so
 * "which region, and in which direction" is a cross-product `posthog-iac` cannot
 * express and the client must pre-join. The underscore is already the separator
 * *inside* both halves (`plan_section_sets`), so the join is a colon, exactly as
 * 9.9.26k reasoned for `space_slot`. `region` and `open` ride alongside, so an
 * ad-hoc query is not forced through the composite.
 *
 * `open` is the direction the region moved **to**, and every caller spells it
 * that way — including the sidebar, whose own state variable is `collapsed`
 * (§2b). One vocabulary across six regions is worth more than each region's
 * local spelling: a `sidebar` series reading backwards from every other series
 * is a trap laid for whoever reads the panel in six months.
 */
export function emitFoldToggled(region: ChromeRegion, open: boolean): void {
  const state = open ? 'open' : 'closed'
  sink(CHROME_EVENTS.foldToggled, { region, open, region_state: `${region}:${state}` })
}

/**
 * One plan-detail view, and how much of it the memory had already shaped.
 *
 * **`closed_count`, not merely `stored_count`, is the number that answers
 * 8.9.26i's claim.** Every one of the four defaults is open and the stored value
 * is the literal `'open'` or `'closed'`, so a key holding `'open'` renders a page
 * *identical* to a page with no key at all — the memory is present and doing
 * nothing. A panel over key presence would show a healthy adoption curve for a
 * memory that changed nothing anybody saw. `stored_count` rides along because it
 * is free once the same four keys have been read, and because the pair separates
 * two populations: `stored > 0, closed = 0` is *engaged and then reverted*,
 * `stored = 0` is *never touched*. Only the first says the affordance was tried.
 *
 * `planId` is the latch key and **does not reach the wire**. The signature is
 * shaped so the id is this module's business rather than the caller's: a caller
 * that had to decide whether to pass it would eventually pass it into
 * `properties`.
 *
 * **A latch and not a `Set`, and A → B → A re-emits.** Returning to a plan is a
 * second view of it — the reading `web_lesson_plan_viewed` already takes, and the
 * one the panel wants; a `Set` would make a session's tenth visit to one plan
 * invisible and turn the denominator into "distinct plans opened". Only the
 * immediate repeat — the remount — is suppressed.
 */
export function emitFoldsRestored(planId: string, closedCount: number, storedCount: number): void {
  if (planId === lastRestoredPlan) return
  lastRestoredPlan = planId
  sink(CHROME_EVENTS.foldsRestored, { closed_count: closedCount, stored_count: storedCount })
}
